import { Box, Link, Typography } from "@mui/material";
import { Grid2 as Grid, Paper, styled } from "@mui/material";
import { addProfileIcon } from "../../../../../assets";
import { theme } from "../../../../../utils";
import {
  Button,
  ImageCropModal,
  TextField,
  SelectBox,
} from "../../../../common";
import { useValidateForm } from "../../../../../hooks";
import { editOrganizationProfileHeaderSchema } from "../../../../../validations";
import { OrganizationProfileInfo } from "../../../../../types";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import {
  isCreateOrganizationModeAtom,
  toastOptionsAtom,
} from "../../../../../atoms";
import { useAtom } from "jotai";
import { COUNTRIES } from "../../../../../constants";

const Item = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1),
  flexGrow: 1,
  borderRadius: "10px",
  boxShadow: "none",
}));

type EditOrganizationProfileHeaderProps = {
  defaultValues?: OrganizationProfileInfo;
  loading?: boolean;
  handleEdit: (data: {
    data: Pick<OrganizationProfileInfo, "name" | "logo" | "country">;
    logo?: File;
  }) => void;
  handleDiscard: () => void;
};

export const EditOrganizationProfileHeader = ({
  defaultValues,
  loading,
  handleEdit,
  handleDiscard,
}: EditOrganizationProfileHeaderProps) => {
  const [, setToastOptions] = useAtom(toastOptionsAtom);
  const [isCreateOrganizationMode] = useAtom(isCreateOrganizationModeAtom);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [uploadedImage, setUploadedImage] = useState<File>();
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string>();
  const uploadInputRef = useRef<HTMLInputElement | null>(null);
  const [nameValue, setNameValue] = useState(defaultValues?.name || "");
  const { register, handleSubmit, errors } = useValidateForm(
    editOrganizationProfileHeaderSchema,
  );

  useEffect(() => {
    if (uploadedImage) {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        setUploadedImageUrl(e.target.result as string);
      };
      reader.readAsDataURL(uploadedImage);
    }
  }, [uploadedImage]);

  useEffect(() => {
    setHasChanges(false);
    setNameValue(defaultValues?.name || "");
  }, [defaultValues]);

  const handleChange = () => {
    setHasChanges(true);
  };

  const handleEditWrapper = (
    data: Pick<OrganizationProfileInfo, "name" | "logo" | "country">,
  ) => {
    if (uploadedImage) {
      const timestamp = new Date().toISOString().replace(/[:.-]/g, "");
      const fileExtension = uploadedImage.name.split(".").pop();
      const fileName = `logo_${timestamp}.${fileExtension}`;
      data.logo = fileName;
    }

    handleEdit({ data, logo: uploadedImage });
  };

  const handleFileUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files ? event.target.files[0] : null;

    if (file && file.type.startsWith("image/")) {
      setUploadedImage(file);
      setCropModalOpen(true);
      if (!isCreateOrganizationMode) {
        setHasChanges(true);
      }
    } else {
      setToastOptions({
        open: true,
        message: "Something went wrong",
        severity: "error",
      });
    }

    event.target.value = "";
  };

  const handleConfirm = (file: File) => {
    setUploadedImage(file);
    setCropModalOpen(false);
    setHasChanges(true);
  };

  return (
    <>
      <Box
        key={defaultValues?.name ?? "new"}
        onSubmit={handleSubmit(handleEditWrapper)}
        component={"form"}
        noValidate
        sx={{ height: "100%" }}
      >
        <Grid
          container
          sx={{
            width: "100%",
            height: "100%",
            minHeight: { xs: "auto", sm: "220px" },
            px: { xs: 2, sm: 4 },
            pt: { xs: 2, sm: 3 },
          }}
        >
          <Grid
            size={{ xs: 12 }}
            sx={{
              px: { xs: 0, sm: 4 },
            }}
          >
            <Item
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: { xs: "center", sm: "flex-start" },
                gap: { xs: 1.5, sm: 1 },
                pl: 0,
              }}
            >
              <Box
                sx={{
                  borderRadius: "50%",
                  overflow: "hidden",
                  width: { xs: 70, sm: 80 },
                  height: { xs: 70, sm: 80 },
                  position: "relative",
                  cursor: "pointer",
                }}
                onClick={() => uploadInputRef.current?.click()}
              >
                <input
                  ref={uploadInputRef}
                  type="file"
                  name="logo"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={handleFileUpload}
                />

                <Box
                  component="img"
                  src={uploadedImageUrl || defaultValues?.logo}
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    opacity: uploadedImageUrl ? 1 : 0.4,
                  }}
                />

                <Box
                  component="img"
                  src={uploadedImageUrl ? uploadedImageUrl : addProfileIcon}
                  alt="Organization logo"
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: 60, sm: 70 },
                    height: { xs: 60, sm: 70 },
                    objectFit: "contain",
                    cursor: "pointer",
                    zIndex: 1,
                    opacity: uploadedImageUrl ? 0 : 0.7,
                  }}
                />
              </Box>

              <Box
                sx={{
                  display: { xs: "none", sm: "block" },
                }}
              >
                <Link
                  href="#"
                  sx={{
                    textDecoration: "underline",
                    textDecorationColor: theme.palette.primary.main,
                  }}
                >
                  <Typography
                    sx={{ color: theme.palette.primary.main }}
                    onClick={() => uploadInputRef.current?.click()}
                  >
                    Profile image / Logo
                  </Typography>
                </Link>

                <Typography sx={{ color: "#a6a6a6" }}>
                  (Image must be in jpeg/png format)
                </Typography>
              </Box>
            </Item>
          </Grid>

          <Grid
            container
            spacing={{ xs: 2, sm: 2 }}
            size={12}
            sx={{
              mt: 2,
              mb: 2,
              px: { xs: 0, sm: 4 },
              width: "100%",
              minWidth: 0,
            }}
          >
            <Grid
              size={{ xs: 12, sm: 6, md: 4 }}
              sx={{
                width: "100%",
                minWidth: 0,
              }}
            >
              <TextField
                register={register}
                defaultValue={defaultValues?.name}
                label="Organization Name"
                name="name"
                error={errors["name"]}
                onChange={(e) => {
                  handleChange();
                  setNameValue(e.target.value);
                }}
                maxLength={200}
                required
              />
            </Grid>

            <Grid
              size={{ xs: 12, sm: 6, md: 4 }}
              sx={{
                width: "100%",
                minWidth: 0,
              }}
            >
              <SelectBox
                register={register}
                label="Country"
                name="country"
                dropdown={COUNTRIES}
                onChange={handleChange}
                defaultValue={defaultValues?.country || "US"}
                required
              />
            </Grid>

            <Grid
              size={{ xs: 12, md: 4 }}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: { xs: "stretch", md: "flex-end" },
                justifyContent: "flex-end",
                gap: 2,
                width: "100%",
              }}
            >
              {!isCreateOrganizationMode && (
                <Button
                  label="Discard"
                  variantType={"secondary"}
                  sx={{
                    padding: "0.5rem",
                    minWidth: { xs: "100%", md: "140px" },
                  }}
                  onClick={handleDiscard}
                />
              )}

              <Button
                type={hasChanges ? "submit" : undefined}
                disabled={!hasChanges || !nameValue || loading}
                label={loading ? "Saving" : "Save Changes"}
                variantType={hasChanges ? "primary" : "secondary"}
                sx={{
                  padding: "0.5rem",
                  minWidth: { xs: "100%", md: "140px" },
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      </Box>

      {uploadedImage && (
        <ImageCropModal
          open={cropModalOpen}
          file={uploadedImage}
          onClose={() => setCropModalOpen(false)}
          onConfirm={handleConfirm}
        />
      )}
    </>
  );
};
